/* YEAR OVER YEAR — the second tab of Overseas Sales (Max 31/10: one module on one SAP-backed source instead of Sales Team
   1's three workbooks). It replaces "Sales Report by Year_2026": the YoY sheets (two years month by month, growth) and the
   year-wise analysis (every year's total, growth amount and rate, the biggest gain and loss). Its numbers are the buyer
   table's own (/api/sales-buyer/summary) — the workbook copies disagreed with each other (XC Sports Jul 2026: 2,226 in
   one, 62,500 in the other and in SAP).

   The comparison that matters while a year is running is LIKE FOR LIKE: Jan → the last month the newer year has, against
   the same months of the older one — a full year against nine months reads as a fall that is not there.
   Max 01/11 ("cần show data theo các sheet"): her Sheet1 too — every year by month with its Total and Growth (this year's
   total as a share of last year's, the way her sheet writes it) and the "26 vs 25" row; the two "Maximum YoY" cards; the
   month-by-month growth bars; the year-wise growth amount and growth rate (two charts, not one with two scales). */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, Cell, Line, ReferenceLine, ComposedChart } from 'recharts';
import { BACKEND_URL } from './backendConfig';
import { useTheme } from './ThemeContext';
import { SERIES, SURFACE } from './explorerModel';
import useAuth from './useAuth';

const API = `${BACKEND_URL}/api/sales-buyer`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/* amounts carry their currency, as on the buyer table (Max 01/11: "thêm đơn vị tiền tệ") */
const usd = (v) => (v == null ? '' : `${v < 0 ? '−' : ''}$${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const usd0 = (v) => (v == null ? '' : `${v < 0 ? '−' : ''}$${Math.abs(Math.round(Number(v))).toLocaleString('en-US')}`);
const compact = (v) => { const x = Math.abs(Number(v) || 0); const s = v < 0 ? '−' : ''; return x >= 1e6 ? `${s}${(x / 1e6).toFixed(1)}M` : x >= 1e3 ? `${s}${Math.round(x / 1e3)}K` : `${s}${Math.round(x)}`; };
const pct = (v) => (v == null || !Number.isFinite(v) ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v * 100).toFixed(1)}%`);
const growth = (a, b) => (a ? (b - a) / a : null);
/* the sign says it, not the colour alone */
const Growth = ({ v }) => <span className={v > 0 ? 'yoy-up' : v < 0 ? 'yoy-dn' : ''}>{v > 0 ? '▲ ' : v < 0 ? '▼ ' : ''}{pct(v)}</span>;

/* a line point's value on a small pill of the surface colour, edged in the line's colour — on bars it stays readable
   (Max 01/11: "data label của chart line bị chìm") */
export const PillLabel = ({ x, y, value, fmt, edge, surface }) => {
  if (value == null || x == null || y == null) return null;
  const text = fmt(value); const w = text.length * 6.2 + 10;
  return (
    <g pointerEvents="none">
      <rect x={x - w / 2} y={y - 24} width={w} height={16} rx={8} fill={surface} stroke={edge} strokeWidth={1} />
      <text x={x} y={y - 12.5} textAnchor="middle" fontSize={10.5} fontWeight={600} fill="var(--portal-text)">{text}</text>
    </g>
  );
};

export default function SalesYoY() {
  const { t } = useTranslation();
  const { resolved } = useTheme();
  const mode = resolved === 'dark' ? 'dark' : 'light';
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [ya, setYa] = useState(null);
  const [yb, setYb] = useState(null);

  /* the forecast Sales types in (Max 01/11: "sale tự nhập nhé") — the newer year's, under her Sheet1's matrix */
  const { permissions = {} } = useAuth();
  const canEdit = permissions.sales_buyer_country === 'edit';
  const [fc, setFc] = useState(null);
  const [fcEdit, setFcEdit] = useState(null);   // the twelve boxes while editing
  const [fcBusy, setFcBusy] = useState(false);
  const [fcErr, setFcErr] = useState('');
  useEffect(() => {
    if (!yb) return;
    setFc(null); setFcEdit(null); setFcErr('');
    fetch(`${API}/forecast?yr=${yb}`, { credentials: 'include' }).then((r) => r.json()).then((d) => setFc(d)).catch(() => setFc(null));
  }, [yb]);
  const saveFc = async () => {
    setFcBusy(true); setFcErr('');
    try {
      const months = fcEdit.map((v) => (String(v).trim() === '' ? null : Number(String(v).replace(/[$,\s]/g, ''))));
      if (months.some((v) => v !== null && !Number.isFinite(v))) throw new Error(t('xSalesBuyer.fcBad'));
      const r = await fetch(`${API}/forecast`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ yr: yb, months }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Error');
      const back = await (await fetch(`${API}/forecast?yr=${yb}`, { credentials: 'include' })).json();
      setFc(back); setFcEdit(null);
    } catch (e) { setFcErr(e.message); } finally { setFcBusy(false); }
  };

  useEffect(() => {
    fetch(`${API}/summary`, { credentials: 'include' })
      .then((r) => r.json().then((d) => {
        if (!r.ok) throw new Error(d.error || 'Error');
        setData(d.years);
        const last = d.years[d.years.length - 1];
        if (last) { setYb(last.year); setYa(last.year - 1); }
      })).catch((e) => setErr(e.message));
  }, []);

  const A = data?.find((y) => y.year === ya);
  const B = data?.find((y) => y.year === yb);
  /* like for like stops at the newer year's last FINISHED month — the month running now holds only the days so far */
  const nowVn = new Date(Date.now() + 7 * 3600000);
  const running = B && B.year === nowVn.getUTCFullYear() && B.lastMonth === nowVn.getUTCMonth() + 1 && B.lastMonth > 1;
  const upTo = B ? (running ? B.lastMonth - 1 : B.lastMonth) : 12;
  const ytd = (y) => (y ? y.months.slice(0, upTo).reduce((s, v) => s + (v || 0), 0) : 0);
  const rows = MON.map((m, i) => ({ month: m, a: A?.months[i] ?? null, b: B?.months[i] ?? null }));
  const yearly = useMemo(() => (data || []).map((y, i, all) => {
    const prev = all[i - 1];
    /* only the running year is partial: 2016 has no December sales and is still a whole year */
    const running = y.year >= new Date(Date.now() + 7 * 3600000).getUTCFullYear();
    return { ...y, label: String(y.year), diff: prev ? y.total - prev.total : null, rate: prev ? growth(prev.total, y.total) : null, partial: running && y.lastMonth < 12 };
  }), [data]);
  /* the biggest gain and loss between two FULL years — a running year against a whole one is not a loss */
  const full = yearly.filter((y) => y.rate != null && !y.partial);
  const best = full.reduce((m, y) => (!m || y.rate > m.rate ? y : m), null);
  const worst = full.reduce((m, y) => (!m || y.rate < m.rate ? y : m), null);
  const ca = SERIES[mode][0]; const cb = SERIES[mode][1];
  const up = SERIES[mode][0]; const down = SERIES[mode][7];
  const ratio = (a, b) => (a ? b / a : null);
  const r0 = (v) => (v == null || !Number.isFinite(v) ? '' : `${(v * 100).toFixed(2)}%`);
  const monthGrowth = rows.map((r) => ({ month: r.month, g: r.a != null && r.b != null && r.a ? (r.b - r.a) / r.a : null })).filter((x) => x.g != null);

  if (err) return <p className="sbc-err" role="alert">{err}</p>;
  if (!data) return <p>{t('xSalesBuyer.loading')}</p>;
  return (
    <div className="yoy">
      <div className="sbc-tools yoy-pick">
        <label className="iv-field"><span>{t('xSalesBuyer.yearA')}</span>
          <select value={ya || ''} onChange={(e) => setYa(Number(e.target.value))} data-yoy-a>{data.map((y) => <option key={y.year} value={y.year}>{y.year}</option>)}</select>
        </label>
        <span className="yoy-vs">vs</span>
        <label className="iv-field"><span>{t('xSalesBuyer.yearB')}</span>
          <select value={yb || ''} onChange={(e) => setYb(Number(e.target.value))} data-yoy-b>{data.map((y) => <option key={y.year} value={y.year}>{y.year}</option>)}</select>
        </label>
      </div>

      {/* her YoY sheet's layout (Max 01/11: "chỉnh lại bố cục như sau"): on the left the month-by-month comparison and its
          growth bars; on the right the two Maximum YoY cards, the year-wise row and the mixed chart. Her Sheet1 below. */}
      <div className="yoy-layout">
        <div className="yoy-left">
          <section className="yoy-card" aria-labelledby="yoy-m">
            <h3 id="yoy-m">{t('xSalesBuyer.yoyCompare')}</h3>
            <table className="pa-table yoy-table yoy-compact" data-yoy-table>
              <thead><tr><th>{t('xSalesBuyer.month')}</th><th className="num">{ya}</th><th className="num">{yb}</th><th className="num">{t('xSalesBuyer.growth')}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.month}>
                    <td>{r.month}</td><td className="num">{usd(r.a)}</td><td className="num">{usd(r.b)}{running && r.month === MON[B.lastMonth - 1] && r.b != null ? ' *' : ''}</td>
                    <td className="num">{r.a != null && r.b != null ? <Growth v={growth(r.a, r.b)} /> : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td>{t('xSalesBuyer.ytdShort', { to: MON[upTo - 1] })}</td><td className="num">{usd(ytd(A))}</td><td className="num">{usd(ytd(B))}</td>
                <td className="num"><Growth v={growth(ytd(A), ytd(B))} /></td></tr></tfoot>
            </table>
            {running && <p className="sbc-muted yoy-note">* {t('xSalesBuyer.soFar', { m: MON[B.lastMonth - 1] })}</p>}
          </section>
          <section className="yoy-card" aria-labelledby="yoy-g" data-yoy-growth>
            <h3 id="yoy-g">{t('xSalesBuyer.yoyComparison')}</h3>
        <ResponsiveContainer width="100%" height={Math.max(160, monthGrowth.length * 26 + 30)}>
          <BarChart data={monthGrowth} layout="vertical" margin={{ top: 4, right: 60, left: 8, bottom: 4 }}>
            <CartesianGrid horizontal={false} stroke="var(--portal-border)" />
            <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11, fill: 'var(--portal-text-muted)' }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="month" width={40} tick={{ fontSize: 11, fill: 'var(--portal-text)' }} tickLine={false} axisLine={false} />
            <ReferenceLine x={0} stroke="var(--portal-text-muted)" />
            <Tooltip formatter={(v) => [pct(v), t('xSalesBuyer.growth')]} cursor={{ fill: 'rgba(127,127,127,.10)' }}
              contentStyle={{ background: 'var(--portal-surface)', border: '1px solid var(--portal-border)', color: 'var(--portal-text)', fontSize: 12 }} />
            <Bar dataKey="g" maxBarSize={16} isAnimationActive={false}>
              {monthGrowth.map((x) => <Cell key={x.month} fill={x.g >= 0 ? up : down} />)}
              <LabelList dataKey="g" position="right" formatter={pct} style={{ fontSize: 11, fill: 'var(--portal-text-muted)' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
          </section>
        </div>

        <div className="yoy-right">
          {best && worst && (
            <div className="iv-tiles yoy-max yoy-center" data-yoy-max>
              <div className="iv-tile"><span>{t('xSalesBuyer.maxLossCard')}</span><b className="yoy-dn">▼ {pct(worst.rate)}</b><em>{worst.year} vs {worst.year - 1}</em></div>
              <div className="iv-tile"><span>{t('xSalesBuyer.maxGainCard')}</span><b className="yoy-up">▲ {pct(best.rate)}</b><em>{best.year} vs {best.year - 1}</em></div>
            </div>
          )}
          <section className="yoy-card" aria-labelledby="yoy-y">
            <h3 id="yoy-y">{t('xSalesBuyer.yearWise')}</h3>
            {/* her one-row table: every year's sales across */}
            <div className="yoy-yrow-wrap">
              <table className="pa-table yoy-yrow" data-yoy-years>
                <thead><tr><th>{t('xSalesBuyer.year')}</th>{yearly.map((y) => <th key={y.year} className="num">{y.year}</th>)}</tr></thead>
                <tbody>
                  <tr><td>{t('xSalesBuyer.salesLegend')}</td>{yearly.map((y) => <td key={y.year} className="num">{usd0(y.total)}</td>)}</tr>
                  <tr><td>{t('xSalesBuyer.growth')}</td>{yearly.map((y) => <td key={y.year} className="num">{y.rate == null ? '' : <Growth v={y.rate} />}</td>)}</tr>
                </tbody>
              </table>
            </div>
        {/* one mixed chart, as her YoY sheet draws it (Max 01/11: "này là chart mix"): the money on the left axis, the growth
            rate on the right — each axis titled, so the two scales cannot be read for each other */}
        <ResponsiveContainer width="100%" height={480}>
          <ComposedChart data={yearly} margin={{ top: 22, right: 8, left: 4, bottom: 0 }} barGap={2} barCategoryGap="26%">
            <CartesianGrid vertical={false} stroke="var(--portal-border)" strokeWidth={1} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--portal-text-muted)' }} tickLine={false} axisLine={{ stroke: 'var(--portal-border)' }} />
            <YAxis yAxisId="usd" tick={{ fontSize: 11, fill: 'var(--portal-text-muted)' }} tickFormatter={compact} width={56} axisLine={false} tickLine={false}
              label={{ value: 'USD', angle: -90, position: 'insideLeft', fill: 'var(--portal-text-muted)', fontSize: 11 }} />
            <YAxis yAxisId="pct" orientation="right" tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11, fill: 'var(--portal-text-muted)' }} width={52} axisLine={false} tickLine={false}
              label={{ value: t('xSalesBuyer.growthRate'), angle: 90, position: 'insideRight', fill: 'var(--portal-text-muted)', fontSize: 11 }} />
            <ReferenceLine yAxisId="usd" y={0} stroke="var(--portal-text-muted)" />
            <Tooltip formatter={(v, n, item) => [item && item.dataKey === 'rate' ? pct(v) : usd(v), n]} cursor={{ fill: 'rgba(127,127,127,.10)' }}
              contentStyle={{ background: 'var(--portal-surface)', border: '1px solid var(--portal-border)', color: 'var(--portal-text)', fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: 'var(--portal-text)' }}>{v}</span>} />
            <Bar yAxisId="usd" dataKey="total" name={t('xSalesBuyer.salesLegend')} fill={ca} radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
            <Bar yAxisId="usd" dataKey="diff" name={t('xSalesBuyer.growthAmount')} fill={cb} radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
            {/* rounded (Max 01/11: "line nên round để chart nhìn mềm hơn"), filled markers, labels on a pill */}
            <Line yAxisId="pct" type="monotone" dataKey="rate" name={t('xSalesBuyer.growthRate')} stroke={SERIES[mode][2]} strokeWidth={2.5}
              dot={{ r: 5, fill: SERIES[mode][2], stroke: SURFACE[mode], strokeWidth: 2 }} activeDot={{ r: 7, fill: SERIES[mode][2], stroke: SURFACE[mode], strokeWidth: 2 }} connectNulls isAnimationActive={false}>
              <LabelList dataKey="rate" content={(p) => <PillLabel {...p} fmt={(v) => `${Math.round(v * 100)}%`} edge={SERIES[mode][2]} surface={SURFACE[mode]} />} />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
            <p className="sbc-muted yoy-note">{t('xSalesBuyer.partialNote')}</p>
          </section>
        </div>
      </div>

      {/* her Sheet1: every year by month, Total, Growth (share of last year's total) and the "B vs A" row */}
      <section className="yoy-card" aria-labelledby="yoy-x" data-yoy-matrix>
        <h3 id="yoy-x">{t('xSalesBuyer.matrixTitle')}</h3>
        <div className="sbc-wrap yoy-mx-wrap">
          <table className="sbc-table yoy-mx" style={{ width: 120 + 12 * 104 + 120 + 80 }}>
            <colgroup><col style={{ width: 120 }} />{MON.map((m) => <col key={m} style={{ width: 104 }} />)}<col style={{ width: 120 }} /><col style={{ width: 80 }} /></colgroup>
            <thead><tr><th className="sbc-c1">{t('xSalesBuyer.year')}</th>{MON.map((m) => <th key={m} className="num">{m}</th>)}<th className="num">{t('xSalesBuyer.total')}</th><th className="num">{t('xSalesBuyer.growth')}</th></tr></thead>
            <tbody>
              {yearly.map((y, i) => (
                <tr key={y.year} className={y.year === ya || y.year === yb ? 'yoy-mx-on' : ''}>
                  <td className="sbc-c1">{y.year}Y</td>
                  {y.months.map((v, j) => <td key={MON[j]} className="num">{v == null ? '' : usd(v)}</td>)}
                  <td className="num sbc-tot">{usd(y.total)}</td>
                  <td className="num">{i ? r0(ratio(yearly[i - 1].total, y.total)) : ''}</td>
                </tr>
              ))}
            </tbody>
            {A && B && (
              <tfoot>
                <tr><td className="sbc-c1">{`${String(yb).slice(2)} vs ${String(ya).slice(2)}`}</td>
                  {rows.map((r) => <td key={r.month} className="num">{r.a != null && r.b != null ? r0(ratio(r.a, r.b)) : ''}</td>)}
                  <td className="num">{r0(ratio(ytd(A), ytd(B)))}</td><td /></tr>
                {/* her Sheet1's two last rows: the forecast Sales typed, and the actual as a share of it */}
                <tr className="yoy-fc" data-yoy-fc>
                  <td className="sbc-c1">{yb}Y FC</td>
                  {MON.map((m, j) => (
                    <td key={m} className="num">
                      {fcEdit
                        ? <input className="yoy-fc-in" inputMode="decimal" value={fcEdit[j]} aria-label={`${yb} ${m} forecast (USD)`} data-yoy-fcin={j}
                          onChange={(e) => setFcEdit((x) => x.map((v, k) => (k === j ? e.target.value : v)))} />
                        : (fc?.months?.[j] == null ? '' : usd(fc.months[j]))}
                    </td>
                  ))}
                  <td className="num">{fc ? usd(fc.months.reduce((s2, v) => s2 + (v || 0), 0)) : ''}</td>
                  <td>
                    {canEdit && !fcEdit && <button type="button" className="pa-btn small" onClick={() => setFcEdit((fc?.months || Array(12).fill(null)).map((v) => (v == null ? '' : String(v))))} data-yoy-fcedit>{t('xSalesBuyer.fcEdit')}</button>}
                  </td>
                </tr>
                <tr className="yoy-fc" data-yoy-fcratio>
                  <td className="sbc-c1">FC vs Actual</td>
                  {MON.map((m, j) => <td key={m} className="num">{fc?.months?.[j] && B?.months[j] != null ? r0(B.months[j] / fc.months[j]) : ''}</td>)}
                  <td className="num">{fc && B ? r0(ratio(fc.months.reduce((s2, v) => s2 + (v || 0), 0), B.total)) : ''}</td><td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {fcEdit && (
          <div className="yoy-fc-bar">
            <span>{t('xSalesBuyer.fcHint', { y: yb })}</span>
            <button type="button" className="pa-btn primary" disabled={fcBusy} onClick={saveFc} data-yoy-fcsave>{t('xSalesBuyer.save')}</button>
            <button type="button" className="pa-btn" disabled={fcBusy} onClick={() => { setFcEdit(null); setFcErr(''); }}>{t('xSalesBuyer.cancel')}</button>
            {fcErr && <span className="sbc-err" role="alert">{fcErr}</span>}
          </div>
        )}
        {fc?.by && !fcEdit && <p className="sbc-muted yoy-note">{t('xSalesBuyer.fcBy', { y: yb, by: fc.by, at: fc.at })}</p>}
        <p className="sbc-muted yoy-note">{t('xSalesBuyer.matrixNote', { a: ya, b: yb, to: MON[upTo - 1] })}</p>
      </section>

    </div>
  );
}
